import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../configureStore";
import axios from "axios";
import { API } from "../../../config";
import { ScenarioData } from "../actions/actionTypes";
import { getLocalizedTextByLocaleVersionTwo } from "../../../utils/localized-text";
import {
  addHours,
  addHoursSameTime,
  setZeroHrs,
} from "../dashboard/dashboardAsyncActions";
import { ColumnsDataTable } from "./DataTableTypes";

export const fetchCropsServiceDataTableFilter = createAsyncThunk(
  "DataFiltersReducer/fetchCropsServiceDataTableFilter",
  async (_, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user } = (store.getState() as RootState).Authentication;

    const token = user?.token ?? "";

    const headers = {
      Authorization: `Bearer ${token}`,
      "Accept-Language": `${locale}`,
    };

    try {
      const response = await axios.get(`${API}/core/cereal-type`, {
        headers,
      });
      const { data } = response;

      const dictionnary = data?.data?.reduce((obj: any, current: any) => {
        obj[current.slug] ??= {
          id: current.id,
          name: current.textIdLocale
            ? current.textIdLocale
            : current.generalName,
          isSelected: false,
          textId: current.textId.id,
        };
        return obj;
      }, {});

      const filtered = Object.keys(dictionnary).map((key) => dictionnary[key]);

      return store.fulfillWithValue(filtered);
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);

export const fetchSBSites = createAsyncThunk(
  "DataFiltersReducer/fetchSBSites",
  async (_, store) => {
    const { user } = (store.getState() as RootState).Authentication;
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const token = user?.token ?? "";
    try {
      const response = await axios.get(
        `${API}/users/organization-geo-point?$select[]=name&$limit=10000`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": `${locale}`,
          },
        },
      );

      const { data } = response;

      const locations = data.data.map((loc) => ({
        id: loc.id,
        name: loc.name,
        totalCapacity: loc.totalCapacity,
        shared: loc.shared ?? false,
      }));

      const filterLoc = locations.filter((v) => v.shared !== true);

      return filterLoc;
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);

export const fetchSBUserNames = createAsyncThunk(
  "DataFiltersReducer/fetchSBUserNames",
  async (_, store) => {
    const { user } = (store.getState() as RootState).Authentication;
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const token = user?.token ?? "";
    try {
      const response = await axios.get(
        `${API}/users/user?$select[]=username&$select[]=id&$limit=10000`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": `${locale}`,
          },
        },
      );

      const { data } = response;
      const usernames = data?.data?.map(
        (usernames: { username: string; id: number }) => ({
          name: usernames.username,
          id: usernames.id,
        }),
      );

      return usernames;
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);

export const fetchSBActionsNames = createAsyncThunk(
  "DataFiltersReducer/fetchSBActionsNames",
  async (_, store) => {
    const { user } = (store.getState() as RootState).Authentication;
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const token = user?.token ?? "";
    try {
      const response = await axios.get(
        `${API}/core/scenario?isSystemScenario=false&deletedAt=null&statusConstantId[$in][]=34&statusConstantId[$in][]=35&$eager=[scopes.[group], cerealType.text.[localizedTexts.[locale]], statusConstant, name.[localizedTexts.[locale]], desc.[localizedTexts.[locale]]]&$limit=10000`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": `${locale}`,
          },
        },
      );

      const { data } = response;

      const actionsValues = data.data.map((row: ScenarioData) => {
        const scenarioNameValue = () => {
          if (row.name) {
            let def = "";
            const arr = row.name.localizedTexts
              .map((c: any) => {
                if (locale === c.locale.value) {
                  return c.translation;
                }
                def = c.translation;
              })
              .filter((val: any) => null != val);
            if (!arr.length) {
              return [def].toString();
            } else {
              return arr.toString();
            }
          } else {
            return "Null";
          }
        };

        const tableRows = {
          name: scenarioNameValue().toLowerCase(),
          id: row.id,
          userFacingNameTextId: row.userFacingNameTextId,
          cerealType: getLocalizedTextByLocaleVersionTwo(
            locale,
            row.cerealType.text.localizedTexts,
          ),
        };
        return tableRows;
      });

      return actionsValues;
    } catch (error) {
      return error;
    }
  },
);

export const exportData = createAsyncThunk(
  "dataExport/exportData",
  async (_, store) => {
    const { user } = (store.getState() as RootState).Authentication;
    const { email, filtersToExport } = (store.getState() as RootState)
      .DataExportReducer;

    const { columns } = (store.getState() as RootState).DataTableReducer;

    const token = user?.token ?? "";
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    /// Temporally export all columns except "id"
    const columnsToExportFiltered = columns?.reduce(
      (acc: any, column: ColumnsDataTable) => {
        if (column.field !== "id") {
          acc.push({
            name: column.field,
            columnType: column.columnType,
            localizedName: column.headerName,
            hidden: false,
          });
        }
        return acc;
      },
      [],
    );

    const body = {
      email: email,
      columns: columnsToExportFiltered,
      filters: {
        datesRange: {
          fromDate: addHours(
            setZeroHrs(filtersToExport.datesRange.fromDate),
          ).toISOString(),
          toDate: addHoursSameTime(
            setZeroHrs(filtersToExport.datesRange.toDate),
          ).toISOString(),
        },
        cropsTextIds:
          filtersToExport.cropsTextIds &&
          filtersToExport.cropsTextIds.length > 0
            ? filtersToExport.cropsTextIds
            : undefined,
        scenarioNamesTextIds:
          filtersToExport.scenarioNamesTextIds &&
          filtersToExport.scenarioNamesTextIds.length > 0
            ? filtersToExport.scenarioNamesTextIds
            : undefined,

        usernames:
          filtersToExport.usernames && filtersToExport.usernames.length > 0
            ? filtersToExport.usernames
            : undefined,

        geoPointNames:
          filtersToExport.geoPointNames &&
          filtersToExport.geoPointNames.length > 0
            ? filtersToExport.geoPointNames
            : undefined,
      },
    };

    try {
      const response = await axios.post(`${API}/portal/data-export`, body, {
        headers,
      });

      return response;
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);

export const exportData2 = createAsyncThunk(
  "dataExport/exportData2",
  async (_, store) => {
    const { user } = (store.getState() as RootState).Authentication;
    const { email, columnsToExport, filtersToExport } = (
      store.getState() as RootState
    ).DataExportReducer;

    const { filterModel } = (store.getState() as RootState).DataTableReducer;

    const token = user?.token ?? "";
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    const body = {
      email: email,
      columns: columnsToExport,
      filters: {
        datesRange: {
          fromDate: addHours(
            setZeroHrs(filtersToExport.datesRange.fromDate),
          ).toISOString(),
          toDate: addHoursSameTime(
            setZeroHrs(filtersToExport.datesRange.toDate),
          ).toISOString(),
        },

        cropsTextIds:
          filtersToExport.cropsTextIds &&
          filtersToExport.cropsTextIds.length > 0
            ? filtersToExport.cropsTextIds
            : undefined,
        scenarioNamesTextIds:
          filtersToExport.scenarioNamesTextIds &&
          filtersToExport.scenarioNamesTextIds.length > 0
            ? filtersToExport.scenarioNamesTextIds
            : undefined,

        usernames:
          filtersToExport.usernames && filtersToExport.usernames.length > 0
            ? filtersToExport.usernames
            : undefined,

        geoPointNames:
          filtersToExport.geoPointNames &&
          filtersToExport.geoPointNames.length > 0
            ? filtersToExport.geoPointNames
            : undefined,
      },
    };

    try {
      const response = await axios.post(`${API}/portal/data-export`, body, {
        headers,
      });

      return response;
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);
